import React, { useState, useContext } from "react"
import {
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Tooltip,
  TableCell,
  TableBody,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Table,
  IconButton,
  TextField,
  Input,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Grid,
  Divider,
} from "@mui/material"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import UserContext from "../../context/UserContext"
import { faecher } from "../../constants/faecher"
import useFetch from "../../hooks/useFetch"
import groupBooksByGrade from "../../functions/groupBooksByGrade"
import useMutation from "../../hooks/useMutation"
import DeleteDialog from "./DeleteDialog"

const CustomPaper = (props) => <Paper elevation={0} {...props} />

const emptyBook = {
  id: "",
  title: "",
  isbn: "",
  priceOriginal: "",
  subject: "",
  grade: "",
  comment: "",
  publisher: "",
}

const Bookmanagement = () => {
  const { userData } = useContext(UserContext)
  const schoolID = userData.school.id

  const [currentClass, setCurrentClass] = useState(5)
  const [createMode, setCreateMode] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [currentBook, setCurrentBook] = useState(emptyBook)

  const [newTitle, setNewTitle] = useState("")
  const [newISBN, setNewISBN] = useState("")
  const [newPrice, setNewPrice] = useState("")
  const [newComment, setNewComment] = useState("")
  const [newVerlag, setNewVerlag] = useState("")
  const [newFach, setNewFach] = useState("")

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [open, setOpen] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState("")

  const { data: bookList, loading: booksLoading, refetch } = useFetch(`/school/${schoolID}/books`)
  const booksByGrade = !bookList || booksLoading ? [] : groupBooksByGrade(bookList.filter((b) => !b.isBundle))

  const { mutate: addBookMutation, loading: addBookLoading } = useMutation("/book")
  const { mutate: updateBookMutation, loading: updateBookLoading } = useMutation("/book", "PUT")
  const { mutate: deleteBookMutation, loading: deleteBookLoading } = useMutation("/book", "DELETE")

  const resetEntry = () => {
    setCreateMode(false)
    setEditMode(false)
    setCurrentBook(emptyBook)
    setNewTitle("")
    setNewISBN("")
    setNewPrice("")
    setNewComment("")
    setNewVerlag("")
    setNewFach("")
  }

  const handleClick = (book) => {
    setCurrentBook(book)
    const { title, isbn, priceOriginal, subject, comment, publisher } = book
    setNewTitle(title)
    setNewISBN(isbn)
    setNewPrice(priceOriginal)
    setNewComment(comment)
    setNewVerlag(publisher)
    setNewFach(subject)
    setEditMode(true)
  }

  const handleAddBook = async (event) => {
    event.preventDefault()
    try {
      await addBookMutation({
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Buch gespeichert")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
  }

  const handleSaveEditedBook = async () => {
    try {
      await updateBookMutation({
        id: currentBook.id,
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Änderungen gespeichert")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
  }

  const handleConfirmDeleteBook = async () => {
    try {
      await deleteBookMutation({
        id: currentBook.id,
        title: newTitle,
        schoolID,
        isbn: newISBN.toString(),
        subject: newFach,
        grade: currentClass,
        priceOriginal: newPrice || 0,
        comment: newComment,
        publisher: newVerlag,
      })

      refetch()

      setOpen(true)
      setSnackbarMsg("Buch gelöscht")
    } catch (e) {
      console.log(e.message)
    }
    resetEntry()
    setShowDeleteConfirm(false)
  }

  const rows = booksByGrade[currentClass] || []
  const loading = booksLoading || addBookLoading || updateBookLoading || deleteBookLoading

  return (
    <>
      <Box sx={{ px: 3, pb: 1, pt: 12 }}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12}>
            <Typography variant="h5" align="left" sx={{ ml: 0, color: "bmsecondary.main", mb: 1, fontWeight: 500 }}>
              {currentClass ? `Bücherliste Klasse ${currentClass}` : "Sonstige Bücher"}
            </Typography>
            <Divider />
          </Grid>
        </Grid>

        <Paper elevation={6} sx={{ mt: 4, borderRadius: "20px" }}>
          <ButtonGroup
            variant="outlined"
            sx={{ width: "100%", height: 36, borderTopLeftRadius: "20px", borderTopRightRadius: "20px", overflow: "auto" }}>
            {[5, 6, 7, 8, 9, 10, 11, 12, 13, 0].map((c) => (
              <Button
                onClick={() => setCurrentClass(c)}
                fullWidth
                key={c}
                sx={{
                  backgroundColor: c !== currentClass ? "bmprimary.main" : "white",
                  color: c === currentClass ? "bmsecondary.main" : "bmsecondary.main",
                  borderColor: "bmprimary.main",
                  "&:hover": { borderColor: "bmprimary.main" },
                  "&:last-child": { borderTopRightRadius: "20px" },
                  "&:first-child": { borderTopLeftRadius: "20px" },
                }}>
                {c ? c : "Extra"}
              </Button>
            ))}
          </ButtonGroup>

          <TableContainer
            sx={{ borderBottomRightRadius: "20px", borderBottomLeftRadius: "20px", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
            component={CustomPaper}>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "25%" }} align="left">
                    Titel
                  </TableCell>
                  <TableCell sx={{ width: "15%" }} align="left">
                    Fach
                  </TableCell>
                  <TableCell sx={{ width: "15%" }} align="left">
                    ISBN
                  </TableCell>
                  <TableCell sx={{ width: "15%" }} align="left">
                    Verlag
                  </TableCell>
                  <TableCell sx={{ width: "10%" }} align="left">
                    Originalpreis
                  </TableCell>
                  <TableCell sx={{ width: "20%" }} align="right">
                    Information
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <>
                    {editMode && row?.id === currentBook?.id ? (
                      <>
                        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                          <TableCell component="th" scope="row">
                            <TextField
                              autoFocus
                              required
                              id="title"
                              name="title"
                              placeholder="Titel *"
                              fullWidth
                              variant="standard"
                              onChange={(e) => setNewTitle(e.target.value)}
                              value={newTitle}
                            />
                          </TableCell>
                          <TableCell align="left">
                            <FormControl variant="standard" sx={{ minWidth: 0, width: "100%", m: 0 }}>
                              <Select
                                required
                                value={newFach}
                                displayEmpty
                                id="fach"
                                name="fach"
                                variant="standard"
                                renderValue={(selected) => {
                                  if (!selected || selected.length === 0) {
                                    return <Typography color="grey.400">Fach *</Typography>
                                  } else {
                                    // const temp = faecher.filter((f) => f.FID === newFach)
                                    // return temp[0]?.Name

                                    return newFach
                                  }
                                }}
                                onChange={(e) => setNewFach(e.target.value)}>
                                <MenuItem disabled value="">
                                  <em>Fach</em>
                                </MenuItem>
                                {faecher.map((f) => (
                                  <MenuItem key={f} value={f}>
                                    {f}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="left">
                            <TextField
                              fullWidth
                              required
                              id="isbn"
                              placeholder="ISBN *"
                              name="isbn"
                              variant="standard"
                              onChange={(event) => {
                                let rawVal = event.target.value
                                let a = rawVal.toString()
                                if (a === " " || a.length === 0 || (a.length === 1 && isNaN(a))) {
                                  setNewISBN("")
                                } else {
                                  setNewISBN(a)
                                  // setNewISBN(parseInt(a))
                                }
                              }}
                              value={newISBN}
                            />
                          </TableCell>
                          <TableCell align="left">
                            <TextField
                              fullWidth
                              required
                              id="verlag"
                              placeholder="Verlag"
                              name="verlag"
                              variant="standard"
                              onChange={(event) => setNewVerlag(event.target.value)}
                              value={newVerlag}
                            />
                          </TableCell>
                          <TableCell align="left">
                            <Input
                              fullWidth
                              placeholder="Org. Preis"
                              id="price"
                              name="price"
                              endAdornment={<InputAdornment position="end">€</InputAdornment>}
                              variant="standard"
                              onChange={(event) => {
                                let rawVal = event.target.value
                                let a = rawVal.toString()
                                let c = a.replace(",", ".")
                                let b = c.replace(/\s/g, "")
                                if (b === " " || b.length === 0 || isNaN(b) || (b.match(".") || []).length > 1) {
                                  setNewPrice(0)
                                } else {
                                  setNewPrice(b)
                                }
                              }}
                              value={newPrice || ""}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              fullWidth
                              placeholder="Information"
                              required
                              id="comment"
                              name="comment"
                              variant="standard"
                              onChange={(event) => setNewComment(event.target.value)}
                              value={newComment}
                            />
                          </TableCell>
                        </TableRow>

                        {/* buttons */}
                        <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell colSpan={6} sx={{ py: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                                width: "100%",
                              }}>
                              <Button
                                variant="contained"
                                sx={{
                                  fontSize: "smaller",
                                  backgroundColor: "bmprimary.main",
                                  color: "white",
                                  "&:hover": { backgroundColor: "bmsecondary.main" },
                                }}
                                onClick={handleSaveEditedBook}
                                disabled={!newTitle || !newISBN || !newFach || newTitle.length === 0 || newISBN.length === 0 || newFach.length === 0}>
                                Speichern
                              </Button>

                              <Button variant="contained" color="warning" sx={{ fontSize: "smaller" }} onClick={() => setShowDeleteConfirm(true)}>
                                Löschen
                              </Button>

                              <Button
                                variant="contained"
                                sx={{
                                  fontSize: "smaller",
                                  backgroundColor: "bmprimary.main",
                                  color: "white",
                                  "&:hover": { backgroundColor: "bmsecondary.main" },
                                }}
                                onClick={resetEntry}>
                                Abbrechen
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow
                        onClick={() => (createMode ? null : handleClick(row))}
                        key={row.id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                        <TableCell component="th" scope="row">
                          {row.title}
                        </TableCell>
                        <TableCell align="left">{row.subject}</TableCell>
                        <TableCell align="left">{row.isbn}</TableCell>
                        <TableCell align="left">{row.publisher}</TableCell>
                        <TableCell align="left">{row.priceOriginal ? `${row.priceOriginal} €` : ""}</TableCell>
                        <TableCell align="right">{row.comment}</TableCell>
                      </TableRow>
                    )}
                  </>
                ))}

                {!createMode ? (
                  <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                    <TableCell component="th" scope="row" sx={{ py: 1 }}>
                      <Tooltip title="Neues Buch anlegen" placement="right" arrow>
                        <IconButton disabled={editMode} onClick={() => setCreateMode(true)}>
                          <AddCircleIcon fontSize="smaller" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                      <TableCell component="th" scope="row">
                        <TextField
                          autoFocus
                          required
                          id="title"
                          name="title"
                          placeholder="Titel *"
                          fullWidth
                          variant="standard"
                          onChange={(e) => setNewTitle(e.target.value)}
                          value={newTitle}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <FormControl variant="standard" sx={{ minWidth: 0, width: "100%", m: 0 }}>
                          <Select
                            required
                            value={newFach}
                            displayEmpty
                            id="fach"
                            name="fach"
                            variant="standard"
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) {
                                return <Typography color="grey.400">Fach *</Typography>
                              } else {
                                // const temp = faecher.filter((f) => f.FID === selected)
                                // return temp[0].Name

                                return selected
                              }
                            }}
                            onChange={(e) => setNewFach(e.target.value)}>
                            <MenuItem disabled value="">
                              <em>Fach</em>
                            </MenuItem>
                            {faecher.map((f) => (
                              <MenuItem key={f} value={f}>
                                {f}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          required
                          id="isbn"
                          placeholder="ISBN *"
                          name="isbn"
                          variant="standard"
                          onChange={(event) => {
                            let rawVal = event.target.value
                            let a = rawVal.toString()
                            if (a === " " || a.length === 0 || (a.length === 1 && isNaN(a))) {
                              setNewISBN("")
                            } else {
                              setNewISBN(parseInt(a))
                            }
                          }}
                          value={newISBN}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          required
                          id="verlag"
                          placeholder="Verlag"
                          name="verlag"
                          variant="standard"
                          onChange={(event) => setNewVerlag(event.target.value)}
                          value={newVerlag}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Input
                          fullWidth
                          placeholder="Org. Preis"
                          id="price"
                          name="price"
                          endAdornment={<InputAdornment position="end">€</InputAdornment>}
                          variant="standard"
                          onChange={(event) => {
                            let rawVal = event.target.value
                            let a = rawVal.toString()
                            let c = a.replace(",", ".")
                            let b = c.replace(/\s/g, "")
                            if (b === " " || b.length === 0 || isNaN(b) || (b.match(".") || []).length > 1) {
                              setNewPrice(0)
                            } else {
                              setNewPrice(b)
                            }
                          }}
                          value={newPrice || ""}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          fullWidth
                          placeholder="Information"
                          required
                          id="comment"
                          name="comment"
                          variant="standard"
                          onChange={(event) => setNewComment(event.target.value)}
                          value={newComment}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}>
                      <TableCell colSpan={6} sx={{ py: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            width: "100%",
                          }}>
                          <Button
                            variant="contained"
                            sx={{
                              fontSize: "smaller",
                              backgroundColor: "bmprimary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "bmsecondary.main" },
                            }}
                            onClick={handleAddBook}
                            disabled={newTitle.length === 0 || newISBN.length === 0 || newFach.length === 0}>
                            Speichern
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              fontSize: "smaller",
                              backgroundColor: "bmprimary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "bmsecondary.main" },
                            }}
                            onClick={resetEntry}>
                            Abbrechen
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {showDeleteConfirm && <DeleteDialog onClose={() => setShowDeleteConfirm(false)} onConfirm={handleConfirmDeleteBook} bookTitle={newTitle} />}
      </Box>

      <Snackbar
        sx={{ color: "bmsecondary.main" }}
        open={open}
        autoHideDuration={10000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return
          setOpen(false)
        }}>
        <Alert onClose={() => setOpen(false)} severity="success" sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Backdrop sx={(theme) => ({ backgroundColor: "rgba(0,0,0,0.1)", zIndex: theme.zIndex.drawer + 1 })} open={loading}>
        <CircularProgress size={60} thickness={5} color="secondary" />
      </Backdrop>
    </>
  )
}

export default Bookmanagement
